<template>
  <div class="px-2">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#1e293b"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#0f172a" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- HEADER -->
    <header class="sa-hero-header mb-4">
      <div class="sa-hero-text">
        <PageBreadcrumb :items="[{ text: 'Platform Admins' }]" class="mb-1" />
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="role-badge-pill">Master Console</span>
        </div>
        <p class="sa-subtitle">
          Provision other Super Admin accounts with full cross-store management access
        </p>
      </div>

      <div class="sa-hero-actions">
        <button class="action-btn outline-btn mr-2" @click="getData()" title="Refresh List">
          <v-icon left size="16" color="#334155">mdi-refresh</v-icon>
          Refresh
        </button>
        <button class="action-btn primary-dark-btn" @click="showAddForm = !showAddForm">
          <v-icon left size="16" color="#ffffff">mdi-plus</v-icon>
          Add Platform Admin
        </button>
      </div>
    </header>

    <!-- ADD FORM -->
    <v-card v-if="showAddForm" outlined class="rounded-lg pa-5 mb-4" color="#FFFFFF">
      <h3 class="dialog-title mb-3">New Platform Admin</h3>
      <v-form ref="form" v-model="valid">
        <v-layout wrap>
          <v-flex xs12 sm6 pa-2>
            <label class="field-label">First Name</label>
            <v-text-field v-model="form.firstname" placeholder="First Name" outlined dense hide-details="auto"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 pa-2>
            <label class="field-label">Last Name</label>
            <v-text-field v-model="form.lastname" placeholder="Last Name" outlined dense hide-details="auto"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 pa-2>
            <label class="field-label">Username <span class="text-danger">*</span></label>
            <v-text-field v-model="form.username" placeholder="Username" outlined dense hide-details="auto"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 pa-2>
            <label class="field-label">Password <span class="text-danger">*</span></label>
            <v-text-field v-model="form.password" type="password" placeholder="Password" outlined dense hide-details="auto"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 pa-2>
            <label class="field-label">Phone Number <span class="text-danger">*</span></label>
            <v-text-field v-model="form.phone" placeholder="Phone Number" outlined dense hide-details="auto"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 pa-2>
            <label class="field-label">Email Address <span class="text-danger">*</span></label>
            <v-text-field v-model="form.email" type="email" placeholder="Email Address" outlined dense hide-details="auto"></v-text-field>
          </v-flex>
        </v-layout>
        <div class="d-flex justify-end gap-2 mt-3">
          <v-btn text class="text-none font-weight-bold" color="#64748b" @click="showAddForm = false">Cancel</v-btn>
          <v-btn
            color="#0F172A"
            dark
            class="text-none font-weight-bold px-4"
            style="border-radius: 8px;"
            :disabled="appLoading"
            @click="submitForm()"
          >
            Create Platform Admin
          </v-btn>
        </div>
      </v-form>
    </v-card>

    <!-- NO DATA STATE -->
    <div v-if="!appLoading && adminList.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#64748b" large class="mb-2">mdi-shield-account-outline</v-icon>
      <div class="no-data-text">No platform admins registered yet.</div>
    </div>

    <!-- TABLE -->
    <v-card v-if="adminList.length > 0" outlined class="rounded-lg overflow-hidden elevation-1 mb-4" color="#FFFFFF">
      <div class="table-container">
        <table class="pro-admin-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 60px;">#</th>
              <th class="text-left">NAME &amp; USERNAME</th>
              <th class="text-left">CONTACT DETAILS</th>
              <th class="text-center" style="width: 100px;">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(value, i) in adminList" :key="value._id || i" class="table-row">
              <td class="text-center font-weight-semibold text-muted">{{ i + 1 }}</td>
              <td class="text-left font-weight-bold">
                <div class="d-flex align-center">
                  <v-avatar color="#F1F5F9" size="34" class="mr-3">
                    <span class="avatar-text">{{ (value.username || 'A').charAt(0).toUpperCase() }}</span>
                  </v-avatar>
                  <div>
                    <div class="store-name">{{ value.firstname || value.username }} {{ value.lastname || '' }}</div>
                    <div class="username-text">
                      <v-icon size="12" color="#64748b" class="mr-1">mdi-account-outline</v-icon>
                      {{ value.username || '-' }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="text-left">
                <div class="contact-line">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-email-outline</v-icon>
                  <span>{{ value.email || '-' }}</span>
                </div>
                <div class="contact-line text-muted mt-1" v-if="value.phone">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
                  <span>{{ value.phone }}</span>
                </div>
              </td>
              <td class="text-center">
                <v-btn
                  x-small
                  icon
                  color="#EF4444"
                  @click="openDeleteDialog(value._id)"
                  title="Remove Platform Admin"
                  :disabled="value._id === currentUserId"
                >
                  <v-icon size="17">mdi-delete-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <!-- DELETE CONFIRMATION DIALOG -->
    <v-dialog v-model="deletedialog" max-width="420px">
      <v-card class="rounded-xl overflow-hidden pa-0" color="#FFFFFF">
        <div class="pa-5 text-center">
          <v-avatar color="#FEE2E2" size="48" class="mb-3">
            <v-icon color="#EF4444" size="24">mdi-alert-outline</v-icon>
          </v-avatar>
          <h3 class="dialog-title mb-1">Remove Platform Admin?</h3>
          <p class="text-body-2 text-muted mb-0">
            Are you sure you want to remove this platform admin account? This action cannot be undone.
          </p>
        </div>
        <v-divider></v-divider>
        <div class="px-5 py-3 dialog-footer d-flex align-center justify-end gap-2">
          <v-btn text height="36" class="text-none font-weight-bold" color="#64748b" @click="deletedialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="#EF4444"
            dark
            height="36"
            elevation="1"
            class="text-none font-weight-bold px-4"
            style="border-radius: 8px;"
            @click="deleteAdmin()"
            :disabled="appLoading"
          >
            Remove
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PlatformAdmins",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      adminList: [],
      showAddForm: false,
      valid: false,
      deletedialog: false,
      curId: null,
      form: {
        firstname: null,
        lastname: null,
        username: null,
        password: null,
        phone: null,
        email: null,
      },
    };
  },
  computed: {
    currentUserId() {
      return localStorage.getItem("id");
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/user/all/superadmins",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: { count: 0, page: 0 },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.adminList = response.data.data || [];
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to load platform admins.";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    resetForm() {
      this.form = {
        firstname: null,
        lastname: null,
        username: null,
        password: null,
        phone: null,
        email: null,
      };
    },
    submitForm() {
      if (!this.form.username || !this.form.password || !this.form.phone || !this.form.email) {
        this.msg = "Please fill username, password, phone and email";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/superadmin/register",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: this.form,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.msg = "Platform admin created successfully";
            this.showsnackbar = true;
            this.showAddForm = false;
            this.resetForm();
            this.getData();
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to create platform admin.";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    openDeleteDialog(id) {
      this.curId = id;
      this.deletedialog = true;
    },
    deleteAdmin() {
      this.appLoading = true;
      axios({
        url: "/user/remove",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: { id: this.curId },
      })
        .then((response) => {
          this.deletedialog = false;
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.msg = response.data.msg || "Platform admin removed";
            this.showsnackbar = true;
            this.getData();
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to remove platform admin.";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.sa-hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.role-badge-pill {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  margin-left: 8px;
}

.sa-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sa-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.outline-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.outline-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.primary-dark-btn {
  background: #0f172a;
  color: #ffffff;
  border: none;
}

.primary-dark-btn:hover {
  background: #1e293b;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.table-container {
  overflow-x: auto;
  width: 100%;
}

.pro-admin-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.pro-admin-table th {
  background: #F8FAFC;
  color: #475569;
  font-size: 11.5px;
  font-weight: 700;
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.pro-admin-table td {
  padding: 12px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #F1F5F9;
  white-space: nowrap;
  color: #0F172A;
}

.table-row:hover td {
  background-color: #F8FAFC !important;
}

.avatar-text {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.store-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #0F172A;
}

.username-text {
  font-size: 12px;
  color: #64748B;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.contact-line {
  font-size: 12.5px;
  color: #334155;
  display: flex;
  align-items: center;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.text-danger {
  color: #ef4444;
}

.dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
}

.dialog-footer {
  background: #F8FAFC;
}
</style>
